import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, doc, getDoc, updateDoc,
  serverTimestamp, onSnapshot
} from '../../firebase/config';
import {
  Calendar, Clock, User, Check, X, MapPin,
  DollarSign, MessageCircle, Play, Star, CreditCard,
  Car, Store, Sparkles, Smartphone, Banknote, AlertCircle,
  CheckCircle2, Plus,
} from 'lucide-react';

const ProfessionalBookingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'pending', label: 'Pending' },
    { id: 'confirmed', label: 'Confirmed' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' },
  ];

  // ---------- Load bookings (real-time) ----------
  useEffect(() => {
    if (!user) return;

    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('professionalId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(bookingsQuery, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setBookings(data);
      setLoading(false);
    }, (error) => {
      console.error('Error loading bookings:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    setFilteredBookings(bookings.filter(b => b.status === activeTab));
  }, [activeTab, bookings]);

  const getTabCount = (status) => bookings.filter(b => b.status === status).length;

  // ---------- ✅ NEW — central total calculation ----------
  // Handles: option price > base price, add-ons, transport fee
  const calculateTotal = (booking) => {
    // If the stored totalPrice exists, use it (source of truth)
    if (typeof booking.totalPrice === 'number' && booking.totalPrice > 0) {
      return booking.totalPrice;
    }

    const base =
      Number(booking.optionPrice) ||
      Number(booking.price) ||
      Number(booking.servicePrice) ||
      0;

    const addonsTotal = Array.isArray(booking.addons)
      ? booking.addons.reduce((sum, a) => sum + Number(a.price || 0), 0)
      : Number(booking.addonsTotal) || 0;

    const transport = Number(booking.transportFee) || 0;

    return base + addonsTotal + transport;
  };

  // ---------- Status transitions ----------
  const handleAccept = async (bookingId) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'confirmed',
        confirmedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error accepting booking:', error);
      alert('Failed to accept booking');
    }
  };

  const handleDecline = async (bookingId) => {
    if (!window.confirm('Decline this booking request?')) return;
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: 'professional',
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error declining booking:', error);
      alert('Failed to decline booking');
    }
  };

  const handleStart = async (bookingId) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'in_progress',
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error starting service:', error);
      alert('Failed to start service');
    }
  };

  const handleComplete = async (bookingId) => {
    if (!window.confirm('Mark this service as completed?')) return;
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: 'professional',
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error completing booking:', error);
      alert('Failed to complete booking');
    }
  };

  const handleMessage = (booking) => {
    if (!booking.clientId) return;
    navigate(`/professional-dashboard/inbox/${booking.clientId}`);
  };

  // ---------- Mark remaining payment collected ----------
  const handleMarkCollected = async (bookingId) => {
    if (!window.confirm('Mark the remaining balance as collected? This will settle the booking.')) return;
    try {
      const ref = doc(db, 'bookings', bookingId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('Booking not found');
        return;
      }
      const data = snap.data();
      const total = calculateTotal(data);

      await updateDoc(ref, {
        amountPaid: total,
        amountDue: 0,
        paymentStatus: 'settled',
        paymentCollectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking collected:', error);
      alert('Failed to update payment');
    }
  };

  // ---------- Mark PayShap received ----------
  const handleMarkPayShapReceived = async (booking) => {
    const confirmed = window.confirm(
      `Confirm you've received the PayShap payment from ${booking.clientName}?\n\nThis will mark the booking as paid.`
    );
    if (!confirmed) return;

    try {
      const ref = doc(db, 'bookings', booking.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('Booking not found');
        return;
      }

      const data = snap.data();
      const total = calculateTotal(data);
      const depositAmount = Number(data.depositAmount || 0);
      const policy = data.paymentPolicy || 'after';

      const isDeposit = policy === 'deposit';
      const newPaid = isDeposit ? depositAmount : total;
      const newDue = Math.max(0, total - newPaid);
      const newStatus = isDeposit ? 'deposit_paid' : 'settled';

      await updateDoc(ref, {
        amountPaid: newPaid,
        amountDue: newDue,
        paymentStatus: newStatus,
        paymentReceivedAt: serverTimestamp(),
        paymentReceivedBy: 'professional',
        updatedAt: serverTimestamp(),
      });

      alert(`Payment received — booking marked as ${newStatus === 'settled' ? 'settled' : 'deposit paid'}`);
    } catch (error) {
      console.error('Error marking PayShap received:', error);
      alert('Failed to update payment');
    }
  };

  // ---------- Mark Cash collected ----------
  const handleMarkCashCollected = async (booking) => {
    const confirmed = window.confirm(
      `Confirm you've received the cash payment from ${booking.clientName}?\n\nThis will settle the booking.`
    );
    if (!confirmed) return;

    try {
      const ref = doc(db, 'bookings', booking.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('Booking not found');
        return;
      }

      const data = snap.data();
      const total = calculateTotal(data);

      await updateDoc(ref, {
        amountPaid: total,
        amountDue: 0,
        paymentStatus: 'settled',
        paymentReceivedAt: serverTimestamp(),
        paymentReceivedBy: 'professional',
        cashCollectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert('Cash recorded — booking settled');
    } catch (error) {
      console.error('Error marking cash collected:', error);
      alert('Failed to update payment');
    }
  };

  // ---------- Formatters ----------
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-ZA', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const formatPrice = (price) => `R${Number(price || 0).toFixed(2)}`;

  const getStatusStyles = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-600 border border-amber-100';
      case 'confirmed': return 'bg-green-50 text-green-600 border border-green-100';
      case 'in_progress': return 'bg-blue-50 text-blue-600 border border-blue-100';
      case 'completed': return 'bg-gray-100 text-gray-600 border border-gray-100';
      case 'cancelled': return 'bg-red-50 text-red-500 border border-red-100';
      default: return 'bg-gray-50 text-gray-500';
    }
  };

  // ---------- Location helpers ----------
  const getLocationLabel = (booking) => {
    if (booking.locationMode === 'mobile') return 'Pro goes to client';
    return 'Client comes to pro';
  };

  const getLocationIcon = (booking) => {
    if (booking.locationMode === 'mobile') return <Car size={14} />;
    return <Store size={14} />;
  };

  const getLocationAddress = (booking) => {
    if (booking.locationMode === 'mobile') return booking.serviceAddress || '';
    return booking.salonAddress || '';
  };

  // ---------- Payment method badge ----------
  const getPaymentMethodBadge = (booking) => {
    const method = booking.paymentMethod;
    if (!method) return null;

    if (method === 'paystack') {
      return {
        icon: <CreditCard size={11} />,
        label: 'Card',
        bg: 'bg-[#F4EEFF]',
        text: 'text-[#AF8BD4]',
      };
    }
    if (method === 'payshap') {
      const isPending = booking.paymentStatus === 'pending_transfer';
      return {
        icon: <Smartphone size={11} />,
        label: isPending ? 'PayShap · Awaiting' : 'PayShap',
        bg: isPending ? 'bg-amber-50' : 'bg-[#F4EEFF]',
        text: isPending ? 'text-amber-600' : 'text-[#AF8BD4]',
      };
    }
    if (method === 'cash') {
      return {
        icon: <Banknote size={11} />,
        label: 'Cash',
        bg: 'bg-blue-50',
        text: 'text-blue-600',
      };
    }
    return null;
  };

  // ---------- Payment summary ----------
  const getPaymentSummary = (booking) => {
    const total = calculateTotal(booking);
    const paid = Number(booking.amountPaid || 0);
    const due = booking.amountDue ?? (total - paid);
    const policy = booking.paymentPolicy;
    const method = booking.paymentMethod;
    const status = booking.paymentStatus;

    if (method === 'payshap' && status === 'pending_transfer') {
      return {
        label: `Client says they've sent ${formatPrice(due || total)}`,
        tone: 'warn',
      };
    }

    if (method === 'cash' && status === 'unpaid') {
      return {
        label: `Cash due at appointment · ${formatPrice(total)}`,
        tone: 'neutral',
      };
    }

    if (!policy) return null;

    if (policy === 'after') {
      return {
        label: `Pay ${formatPrice(total)} at appointment`,
        tone: 'neutral',
      };
    }
    if (policy === 'full') {
      return {
        label: booking.paymentStatus === 'paid_in_full' || booking.paymentStatus === 'settled'
          ? `Paid in full · ${formatPrice(total)}`
          : `Awaiting full payment · ${formatPrice(total)}`,
        tone: booking.paymentStatus === 'paid_in_full' || booking.paymentStatus === 'settled' ? 'good' : 'warn',
      };
    }
    if (policy === 'deposit') {
      if (booking.paymentStatus === 'settled') {
        return { label: `Settled · ${formatPrice(total)}`, tone: 'good' };
      }
      if (booking.paymentStatus === 'deposit_paid') {
        return {
          label: `Deposit paid ${formatPrice(paid)} · Balance ${formatPrice(due)}`,
          tone: 'warn',
        };
      }
      return {
        label: `Deposit ${formatPrice(booking.depositAmount || 0)} · Balance ${formatPrice(due)}`,
        tone: 'neutral',
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-20">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Bookings</h1>
        <p className="text-gray-500 mt-1">Manage all your client appointments</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`bg-white rounded-2xl p-4 border cursor-pointer transition-all ${
              activeTab === tab.id
                ? 'border-[#CDBCE7]'
                : 'border-gray-100 hover:border-[#CDBCE7]'
            }`}
          >
            <p className="text-2xl font-bold text-gray-900">{getTabCount(tab.id)}</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">
              {tab.label}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors border ${
              activeTab === tab.id
                ? 'bg-[#010101] text-white border-[#010101]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
            }`}
          >
            {tab.label}
            {getTabCount(tab.id) > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${
                activeTab === tab.id
                  ? 'bg-white/20 text-white'
                  : 'bg-[#F4EEFF] text-[#AF8BD4]'
              }`}>
                {getTabCount(tab.id)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bookings list */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
          <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar size={32} className="text-[#AF8BD4]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
            No {activeTab.replace('_', ' ')} bookings
          </h3>
          <p className="text-gray-400 text-sm">
            {activeTab === 'pending'
              ? 'New booking requests will appear here'
              : `Your ${activeTab.replace('_', ' ')} bookings will show here`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => {
            const payment = getPaymentSummary(booking);
            const paymentBadge = getPaymentMethodBadge(booking);
            const total = calculateTotal(booking);
            const hasTransport = Number(booking.transportFee || 0) > 0;
            const hasOption = !!booking.optionName;
            const hasAddons = Array.isArray(booking.addons) && booking.addons.length > 0;
            const addonsTotal = hasAddons
              ? booking.addons.reduce((s, a) => s + Number(a.price || 0), 0)
              : 0;

            const isPayShapPending =
              booking.paymentMethod === 'payshap' && booking.paymentStatus === 'pending_transfer';
            const isCashPending =
              booking.paymentMethod === 'cash' && booking.paymentStatus === 'unpaid';
            const isCardWithBalance =
              booking.paymentMethod === 'paystack' &&
              booking.paymentStatus !== 'settled' &&
              booking.paymentStatus !== 'paid_in_full';

            return (
              <div
                key={booking.id}
                className="bg-white rounded-3xl p-5 border border-gray-100 hover:border-[#CDBCE7] transition-colors"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-14 h-14 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0 overflow-hidden">
                      {booking.clientImage ? (
                        <img
                          src={booking.clientImage}
                          alt={booking.clientName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={22} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">
                        {booking.clientName || 'Client'}
                      </h3>
                      {booking.clientEmail && (
                        <p className="text-xs text-gray-500 truncate">{booking.clientEmail}</p>
                      )}
                      {booking.clientPhone && (
                        <p className="text-xs text-gray-400 truncate">{booking.clientPhone}</p>
                      )}
                    </div>
                  </div>
                  <div className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shrink-0 ${getStatusStyles(booking.status)}`}>
                    {booking.status?.replace('_', ' ') || 'unknown'}
                  </div>
                </div>

                {/* Service + option */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 text-sm text-gray-900 flex-wrap">
                    <Sparkles size={16} className="text-[#AF8BD4] shrink-0" />
                    <span className="font-bold truncate">
                      {booking.serviceTitle || booking.serviceName || 'Service'}
                    </span>
                    {hasOption && (
                      <span className="text-[#AF8BD4] font-bold shrink-0">
                        · {booking.optionName}
                      </span>
                    )}
                    {paymentBadge && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${paymentBadge.bg} ${paymentBadge.text}`}>
                        {paymentBadge.icon}
                        {paymentBadge.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={16} className="text-gray-400 shrink-0" />
                    <span className="font-medium">
                      {formatDate(booking.date)} at {booking.time || 'TBD'}
                    </span>
                  </div>

                  {booking.duration && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock size={16} className="text-gray-400 shrink-0" />
                      <span className="font-medium">{booking.duration}</span>
                    </div>
                  )}

                  {booking.locationMode && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 md:col-span-2">
                      <div className="text-[#AF8BD4] shrink-0">{getLocationIcon(booking)}</div>
                      <span className="font-medium truncate">
                        {getLocationLabel(booking)}
                        {getLocationAddress(booking) && (
                          <> · <span className="text-gray-500">{getLocationAddress(booking)}</span></>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* ✅ Price breakdown — now with add-ons */}
                <div className="bg-[#FCF8F9] rounded-2xl p-3 mb-3">
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {hasOption ? `${booking.optionName} price` : 'Service price'}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatPrice(booking.optionPrice || booking.price || booking.servicePrice)}
                      </span>
                    </div>

                    {/* ✅ Add-ons line items */}
                    {hasAddons && booking.addons.map((addon, idx) => (
                      <div key={addon.id || idx} className="flex justify-between">
                        <span className="text-gray-500 flex items-center gap-1.5">
                          <Plus size={11} className="text-[#AF8BD4]" />
                          {addon.name}
                        </span>
                        <span className="font-medium text-[#AF8BD4]">
                          +{formatPrice(addon.price)}
                        </span>
                      </div>
                    ))}

                    {hasTransport && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 flex items-center gap-1.5">
                          <Car size={12} className="text-[#AF8BD4]" />
                          Transport fee
                        </span>
                        <span className="font-medium text-[#AF8BD4]">
                          +{formatPrice(booking.transportFee)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between pt-1.5 border-t border-gray-100">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-gray-900">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment summary */}
                {payment && (
                  <div className={`mb-4 rounded-2xl p-3 flex items-center gap-2 text-xs font-medium ${
                    payment.tone === 'good' ? 'bg-green-50 text-green-700' :
                    payment.tone === 'warn' ? 'bg-amber-50 text-amber-700' :
                    'bg-[#FCF8F9] text-gray-600'
                  }`}>
                    {isPayShapPending ? (
                      <AlertCircle size={14} className="shrink-0" />
                    ) : (
                      <CreditCard size={14} className="shrink-0" />
                    )}
                    <span>{payment.label}</span>
                  </div>
                )}

                {/* Notes */}
                {booking.notes && (
                  <div className="bg-[#FCF8F9] rounded-2xl p-3 mb-4">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">
                      Client Notes
                    </p>
                    <p className="text-sm text-gray-700 italic">"{booking.notes}"</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-50">

                  {/* PENDING */}
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAccept(booking.id)}
                        className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                      >
                        <Check size={16} /> Accept
                      </button>
                      <button
                        onClick={() => handleDecline(booking.id)}
                        className="px-5 py-2.5 bg-red-50 text-red-500 text-sm font-bold rounded-full hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <X size={16} /> Decline
                      </button>
                      <button
                        onClick={() => handleMessage(booking)}
                        className="px-5 py-2.5 bg-[#FCF8F9] text-gray-600 text-sm font-bold rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 border border-gray-100"
                      >
                        <MessageCircle size={16} /> Message
                      </button>
                    </>
                  )}

                  {/* CONFIRMED */}
                  {booking.status === 'confirmed' && (
                    <>
                      {isPayShapPending && (
                        <button
                          onClick={() => handleMarkPayShapReceived(booking)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle2 size={16} /> Mark PayShap Received
                        </button>
                      )}

                      {!isPayShapPending && (
                        <button
                          onClick={() => handleStart(booking.id)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Play size={16} /> Start Service
                        </button>
                      )}

                      <button
                        onClick={() => handleMessage(booking)}
                        className="px-5 py-2.5 bg-[#FCF8F9] text-gray-600 text-sm font-bold rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 border border-gray-100"
                      >
                        <MessageCircle size={16} /> Message
                      </button>
                    </>
                  )}

                  {/* IN PROGRESS */}
                  {booking.status === 'in_progress' && (
                    <>
                      {isCashPending && (
                        <button
                          onClick={() => handleMarkCashCollected(booking)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Banknote size={16} /> Mark Cash Collected
                        </button>
                      )}

                      <button
                        onClick={() => handleComplete(booking.id)}
                        className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                      >
                        <Check size={16} /> Mark Completed
                      </button>
                      <button
                        onClick={() => handleMessage(booking)}
                        className="px-5 py-2.5 bg-[#FCF8F9] text-gray-600 text-sm font-bold rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 border border-gray-100"
                      >
                        <MessageCircle size={16} /> Message
                      </button>
                    </>
                  )}

                  {/* COMPLETED */}
                  {booking.status === 'completed' && (
                    <>
                      {isCashPending && (
                        <button
                          onClick={() => handleMarkCashCollected(booking)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Banknote size={16} /> Mark Cash Collected
                        </button>
                      )}

                      {isPayShapPending && (
                        <button
                          onClick={() => handleMarkPayShapReceived(booking)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle2 size={16} /> Mark PayShap Received
                        </button>
                      )}

                      {!isCashPending && !isPayShapPending && isCardWithBalance && (
                        <button
                          onClick={() => handleMarkCollected(booking.id)}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <CreditCard size={16} /> Mark Payment Collected
                        </button>
                      )}

                      {booking.clientRating?.stars ? (
                        <div className="flex items-center gap-2 text-sm text-amber-600 font-medium">
                          <Star size={14} className="fill-amber-500 text-amber-500" />
                          Rated {booking.clientRating.stars}/5 by client
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 font-medium">
                          Awaiting client review
                        </div>
                      )}
                    </>
                  )}

                  {/* CANCELLED */}
                  {booking.status === 'cancelled' && (
                    <div className="text-sm text-gray-400 font-medium">
                      This booking was cancelled
                      {booking.cancelledBy && ` by ${booking.cancelledBy}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfessionalBookingsPage;